import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export default function DocCapture({ label, value, filename, onChange, allowPdf = false, maxMB = 5, testId }) {
  const { t } = useApp();
  const camRef = useRef();
  const fileRef = useRef();
  const [name, setName] = useState(filename || "");

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > maxMB * 1024 * 1024) return toast.error(`${t.doctorAuth.fileTooLarge} (${maxMB} MB)`);
    const reader = new FileReader();
    reader.onload = () => { setName(f.name); onChange(reader.result, f.name); };
    reader.readAsDataURL(f);
  };

  const remove = () => { setName(""); onChange("", ""); };
  const isImage = value && value.startsWith("data:image");

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label>{label}</Label>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
             onChange={handleFile} data-testid={`${testId}-camera-input`}/>
      <input ref={fileRef} type="file" accept={allowPdf ? "image/*,application/pdf" : "image/*"} className="hidden"
             onChange={handleFile} data-testid={`${testId}-file-input`}/>
      {!value ? (
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => camRef.current?.click()}
                  data-testid={`${testId}-camera-btn`}
                  className="h-10 gap-2 border-blue-200 text-blue-700 hover:bg-blue-50">
            <Camera size={14} className="shrink-0"/><span className="truncate text-sm">{t.doctorAuth.takePhoto}</span>
          </Button>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                  data-testid={`${testId}-upload-btn`}
                  className="h-10 gap-2 border-blue-200 text-blue-700 hover:bg-blue-50">
            <Upload size={14} className="shrink-0"/><span className="truncate text-sm">{t.doctorAuth.uploadFile}</span>
          </Button>
        </div>
      ) : (
        <div data-testid={`${testId}-preview`}
             className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            {isImage
              ? <img src={value} alt="" data-testid={`${testId}-thumb`} className="h-12 w-12 rounded object-cover border border-emerald-200 shrink-0"/>
              : <FileText size={18} className="text-emerald-600 shrink-0"/>}
            <span className="text-xs text-emerald-800 font-medium truncate" title={name} data-testid={`${testId}-filename`}>
              {name || t.doctorAuth.fileLoaded}
            </span>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={remove} data-testid={`${testId}-remove-btn`}
                  className="h-7 px-2 text-xs text-red-700 hover:bg-red-50 shrink-0">
            <X size={12} className="mr-1"/>{t.doctorAuth.removeFile}
          </Button>
        </div>
      )}
    </div>
  );
}
